import moment from "moment";
import { PlatformWidget, HealthStatus } from "./types";
import { AlertSeverity, PlatformAlertRow } from "./Alerts.types";
import { SEVERITY, TIMEZONE_MAP } from "./Constants";
import { getTopSegments, deriveStatusKey } from "./PlatformHealthUtils";

export type MetricKey = Exclude<HealthStatus, "HEALTHY">;

export const DEFAULT_TIME_RANGE = "today";

export const normalizeStatus = (s: unknown): string =>
  (s == null ? "" : String(s)).trim().toUpperCase();

export const mapAlertCounts = (counts: any) => {
  let critical = 0,
    major = 0,
    minor = 0,
    warning = 0,
    info = 0,
    platformAffected = 0;

  if (Array.isArray(counts)) {
    counts.forEach((c: any) => {
      const s = String(c?.status ?? "").toLowerCase();
      const val = Number(c?.count ?? 0);
      if (s === "critical") critical = val;
      if (s === "major") major = val;
      if (s === "minor") minor = val;
      if (s === "warning") warning = val;
      if (s === "ok" || s === "info") info = val;
      if (s === "platformaffected" || s === "platform_affected") platformAffected = val;
    });
  } else {
    critical = Number(counts?.critical ?? 0);
    major = Number(counts?.major ?? 0);
    minor = Number(counts?.minor ?? 0);
    warning = Number(counts?.warning ?? 0);
    info = Number(counts?.info ?? counts?.ok ?? 0);
    platformAffected = Number(counts?.platformAffected ?? 0);
  }

  const total = critical + major + minor + warning + info + platformAffected;
  return { critical, major, minor, warning, info, platformAffected, total };
};

export const mapResponseToPlatformWidgets = (res: any): PlatformWidget[] => {
  let rawPlatforms: any[] = [];
  if ((res?.type === "alertPlatform" || res?.type === "realtimePlatform") && res?.platform) {
    rawPlatforms = [
      {
        ...res.platform,
        _rawType: res.type,
      },
    ];
  }
  else if (res && Array.isArray(res.platform)) {
    rawPlatforms = res.platform;
  } else if (res?.platform && !Array.isArray(res)) {
    rawPlatforms = [res.platform];
  }
  else if (Array.isArray(res)) {
    rawPlatforms = res
      .filter((item: any) => (item?.type === "alertPlatform" || item?.type === "realtimePlatform") && item?.platform)
      .map((item: any) => {
        return {
          ...item.platform,
          _rawType: item.type
        };
      });
  } else if ((res?.type === "alertPlatform" || res?.type === "realtimePlatform") && res?.platform) {
    rawPlatforms = [{...res.platform, _rawType: res.type}];
  }

  return rawPlatforms
    .map((plat: any, idx: number) => {
      const platformId = plat?.platformId ?? "";
      const platformName = plat?.platformName ?? "";
      const group = plat?.platformType ?? "";
      const status = normalizeStatus(plat?.status);

      const isRealTime = plat?._rawType === "realtimePlatform";
      const alertCounts = mapAlertCounts(plat?.severityCount || plat?.activeAlertCounts);

      const impactedResources: any[] = Array.isArray(plat?.impactedResources)
        ? plat.impactedResources
        : [];
      
      const allResources: any[] = Array.isArray(plat?.allResources)
        ? plat.allResources
        : [];

      return {
        order: plat?.order ?? idx + 1,
        platformId,
        platformName,
        group,
        status,
        alertSource: plat?.alertSource ?? "",
        startDate: plat?.startDate ?? "",
        alertCounts: {
          ...alertCounts,
          platformAffected: alertCounts.platformAffected || 0,
        },
        previewAlerts: impactedResources.map((r, rIdx) => ({
          alertId:
          r?.alertId ||
          `${platformId}-${r?.resourceName || r?.metric_name || "res"}-${r?.issueSummary || rIdx}`,
          resourceName: r?.resourceName || r?.kpi_name || r?.metric_name || "",
          issueSummary: r?.issueSummary || r?.kpi_title || "",
          severity: normalizeStatus(r?.status || r?.severity),
          durationLabel: r?.alertAge ?? "",
          alertTime: r?.alertTime ?? "",
          status: normalizeStatus(r?.status || r?.severity),
        })),
        isRealTime,
        kpiItems: impactedResources,
        allKpiItems: allResources,
        moreAlertsCount: impactedResources.length,
      } as PlatformWidget;
    })
    .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
};

export const widgetMatchesSearch = (w: PlatformWidget, query: string): boolean => {
  if (!query) return false;
  if ((w.platformName || "").toLowerCase().includes(query)) return true;

  // Search through previewAlerts (works for both alertPlatform and realtimePlatform)
  const matchesPreview = (w.previewAlerts ?? []).some(
    (a) =>
      (a.resourceName || "").toLowerCase().includes(query) ||
      (a.issueSummary || "").toLowerCase().includes(query)
  );
  if (matchesPreview) return true;

  // For realtime platforms, also search string fields in kpis (except metadata like severity)
  const searchInItems = (items: any[]): boolean => {
    const forbiddenFields = ['severity', 'status', 'crossed_threshold', 'priority_order', 'rank', 'unit', 'color'];
    return items.some((item: any) =>
      item && Object.entries(item).some(([key, val]) => 
        !forbiddenFields.includes(key) && 
        typeof val === "string" && 
        val.toLowerCase().includes(query)
      )
    );
  };

  if ((w as any).kpiItems?.length && searchInItems((w as any).kpiItems)) return true;
  if ((w as any).allKpiItems?.length && searchInItems((w as any).allKpiItems)) return true;

  return false;
};

export const getDominantTopSeg = (w: PlatformWidget) => {
  const top = getTopSegments(w);
  if (top.length === 0) return null;

  return [...top].sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return a.severityRank - b.severityRank;
  })[0];
};

export const getSectionPlaceholderCount = (length: number, columns: number) => {
  if (length === 0) return 0;
  const remainder = length % columns;
  return remainder === 0 ? 0 : columns - remainder;
};

export const severityBadgeColor = (sev: AlertSeverity): string => {
  switch (sev) {
    case SEVERITY.CRITICAL:
      return "#F04438";
    case SEVERITY.MAJOR:
      return "#F79009";
    case SEVERITY.MINOR:
      return "#EAAA08";
    case SEVERITY.WARNING:
      return "#FFCC00";
    case SEVERITY.INFO:
      return "#2E90FA";
    default:
      return "#ACE000";
  }
};

export const statusPillColor = (status: string): string => {
  switch (status) {
    case SEVERITY.CRITICAL:
      return "#F04438";
    case SEVERITY.MAJOR:
      return "#F79009";
    case SEVERITY.MINOR:
      return "#EAAA08";
    case SEVERITY.WARNING:
      return "#FFCC00";
    case SEVERITY.INFO:
      return "#2E90FA";
    default:
      return "#ACE000";
  }
};

const formatAlertTime = (value: any, ianaTimezone: string = TIMEZONE_MAP.CST): string => {
  if (!value || value === "-" || (typeof value === "string" && value.toLowerCase() === "invalid date")) return "-";
  
  // Supported parsing formats, especially for "YY" in backend dates
  const formats = ["MM-DD-YY HH:mm", "MM-DD-YYYY HH:mm:ss", "MM-DD-YYYY HH:mm", "MM/DD/YYYY HH:mm:ss"];
  
  if (typeof value === "number") {
    // Treat numeric timestamps as absolute and format in target zone
    return moment(value).tz(ianaTimezone).format("MM/DD/YYYY HH:mm:ss");
  }
  
  if (typeof value === "string") {
    // Attempt parsing in the context of the target timezone first to avoid local shifts
    const m = moment.tz(value, formats, ianaTimezone);
    if (m.isValid()) {
      return m.format("MM/DD/YYYY HH:mm:ss");
    }
    
    // Fallback to generic parsing if specific formats fail
    const genericM = moment(value);
    if (genericM.isValid()) {
      return genericM.tz(ianaTimezone).format("MM/DD/YYYY HH:mm:ss");
    }
  }
  
  return value;
};

export const transformAlerts = (detail: any[], ianaTimezone: string = TIMEZONE_MAP.CST): PlatformAlertRow[] => {
  return detail.map((d: any) => ({
    ...d,
    alertId: d.alertNo,
    alertUrl: d.alertLink,
    lastEventTime: formatAlertTime(d.lastEventTime, ianaTimezone),
    resource: { resourceName: d.resource || "-" },
    severity:
      d.severity === "OK" || !d.severity
        ? SEVERITY.INFO
        : (String(d.severity).toUpperCase() as AlertSeverity),
    issue: { summary: d.issue || "-" },
    description: d.description || "-",
    durationLabel: formatAlertTime(d.alertTime, ianaTimezone),
    owner: {
      team: d.owner || "-",
      assignmentGroup: d.assignmentGroup || "-",
    },
    acknowledged: d.acknowledged,
    ticket: d.ticket
      ? {
          provider: "SERVICENOW",
          ticketId: d.ticket,
          ticketUrl: d.incidentLink,
        }
      : undefined,
    state: d.state || "-",
    resourceGroup: d.resourceGroup || "-",
    subscriptionId: d.subscriptionId || "-",
    subscriptionName: d.subscriptionName || "-",
    alertAge: d.alertAge || "-",
    uuid: d.uuid || "-",

  }));
};

export const getHighestSeverity = (sum: any): AlertSeverity | typeof SEVERITY.INFO => {
  if (!sum) return SEVERITY.INFO;
  if (sum.critical > 0) return SEVERITY.CRITICAL;
  if (sum.major > 0) return SEVERITY.MAJOR;
  if (sum.minor > 0) return SEVERITY.MINOR;
  if (sum.warning > 0) return SEVERITY.WARNING;
  return SEVERITY.INFO;
};

export const buildSummaryAggregate = (widgets: (PlatformWidget | null)[]): Record<MetricKey, number> => {
  const totals: Record<MetricKey, number> = {
    CRITICAL: 0,
    MAJOR: 0,
    MINOR: 0,
    WARNING: 0,
    INFO: 0,
    PLATFORM_AFFECTED: 0,
  };

  widgets.forEach((w) => {
    if (!w) return;
    const counts = w.alertCounts;
    if (counts) {
      totals.CRITICAL += counts.critical || 0;
      totals.MAJOR += counts.major || 0;
      totals.MINOR += counts.minor || 0;
      totals.WARNING += counts.warning || 0;
      totals.INFO += counts.info || 0;
    }

    if (deriveStatusKey(w) !== "HEALTHY") {
      totals.PLATFORM_AFFECTED += 1;
    }
  });

  return totals;
};
